import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { DEMO_ACCOUNTS } from '../utils/demoAccounts';
import { PUBLIC_JOIN_LINKS } from '../constants/publicNav';
import { getPostLoginPath } from '../utils/postLoginRoute';
import AuthSplitShell from '../components/AuthSplitShell';

const FormTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-size: clamp(1.45rem, 3.5vw, 1.85rem);
  font-weight: 700;
  color: #0f172a;
  text-align: center;
`;

const FormSubtitle = styled.p`
  margin: 0 0 1.5rem;
  text-align: center;
  color: #64748b;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #334155;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 0.9rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: #0d9488;
  }

  &.error {
    border-color: ${(props) => props.theme.colors.danger};
  }
`;

const Button = styled.button`
  background: #0f766e;
  color: white;
  border: none;
  padding: 0.85rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0d9488;
  }

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: ${(props) => props.theme.colors.dangerLight};
  color: ${(props) => props.theme.colors.danger};
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: ${(props) => props.theme.colors.successLight};
  color: ${(props) => props.theme.colors.success};
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
`;

const DemoBox = styled.div`
  margin-top: 1.35rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
  background: #f8fafc;

  h4 {
    margin: 0 0 0.55rem;
    font-size: 0.85rem;
    color: #0f172a;
  }
`;

const DemoRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0;
  border-top: 1px solid #e2e8f0;
  font-size: 0.85rem;
  min-width: 0;

  > span {
    min-width: 0;
    overflow-wrap: break-word;
    flex: 1 1 auto;
    color: #475569;

    strong {
      color: #0f172a;
    }
  }

  button {
    background: transparent;
    color: #0f766e;
    border: 1px solid #0f766e;
    border-radius: 6px;
    padding: 0.3rem 0.6rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 1.25rem;
  color: #64748b;
  font-size: 0.9rem;

  a {
    color: #0f766e;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const JoinRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.35rem 0.75rem;
  margin-top: 0.65rem;
`;

const LOGIN_FEATURES = [
  {
    icon: '1',
    title: 'Seven interview modes',
    description: 'Voice, video, written, MCQ, aptitude, coding, and typing — practice against a real JD.',
  },
  {
    icon: '2',
    title: 'Consultancy CRM & Talent',
    description: 'Pipeline desks, client companies, and opt-in talent search with unlock packs.',
  },
  {
    icon: '3',
    title: 'Institutions & EdTech partners',
    description: 'Campus cohorts on shared reports; partners list external tutorials for weak topics.',
  },
];

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { login, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';
  const flashMessage = location.state?.message || '';
  const needsVerify =
    /verify your email|not active/i.test(authError || '') || /verify/i.test(flashMessage || '');

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const finishLogin = (result) => {
    if (result.success) {
      const dest = getPostLoginPath(result.user, from);
      navigate(dest, { replace: true, state: { firstSession: !from || from === '/dashboard' } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    const result = await login(formData.username, formData.password);
    setIsLoading(false);
    finishLogin(result);
  };

  const handleDemoLogin = async (account) => {
    setFormData({ username: account.username, password: account.password });
    setErrors({});
    setIsLoading(true);
    const result = await login(account.username, account.password);
    setIsLoading(false);
    finishLogin(result);
  };

  return (
    <AuthSplitShell
      title="Welcome back to EnterWeU"
      tagline="Sign in to practice interviews, run hiring desks, manage campus cohorts, or publish EdTech tutorials."
      features={LOGIN_FEATURES}
    >
      <FormTitle>Sign in</FormTitle>
      <FormSubtitle>Use your account or a one-click demo login</FormSubtitle>

      {flashMessage && <SuccessMessage>{flashMessage}</SuccessMessage>}
      {authError && <ErrorMessage>{authError}</ErrorMessage>}
      {needsVerify && (
        <LinkText style={{ marginTop: 0, marginBottom: '1rem' }}>
          <Link to="/verify-email">Open email verification</Link>
          {' · '}
          <Link to="/set-password">Added by a consultancy or institution? Send me a new invite link</Link>
        </LinkText>
      )}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="username">Username</Label>
          <Input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className={errors.username ? 'error' : ''}
            placeholder="Enter your username"
            autoComplete="username"
          />
          {errors.username && <ErrorMessage>{errors.username}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={errors.password ? 'error' : ''}
            placeholder="Enter your password"
            autoComplete="current-password"
          />
          {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
        </FormGroup>

        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Sign In'}
        </Button>
      </Form>

      {!needsVerify && (
        <LinkText style={{ marginTop: '0.75rem' }}>
          Added by a consultancy or institution?{' '}
          <Link to="/set-password">Send me a new invite link</Link>
        </LinkText>
      )}

      <DemoBox>
        <h4>Demo logins</h4>
        {DEMO_ACCOUNTS.map((account) => (
          <DemoRow key={account.username}>
            <span>
              <strong>{account.label}</strong>
              <br />
              {account.username} / {account.password}
              {account.hint ? (
                <>
                  <br />
                  <em style={{ fontStyle: 'normal', color: '#94a3b8' }}>{account.hint}</em>
                </>
              ) : null}
            </span>
            <button type="button" disabled={isLoading} onClick={() => handleDemoLogin(account)}>
              Sign in
            </button>
          </DemoRow>
        ))}
      </DemoBox>

      <LinkText>
        Don&apos;t have an account?
        <JoinRow>
          {PUBLIC_JOIN_LINKS.map((link) => (
            <Link key={link.to} to={link.to}>
              {link.label}
            </Link>
          ))}
        </JoinRow>
      </LinkText>
    </AuthSplitShell>
  );
};

export default Login;
